import { useEffect, useState } from "react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LeadLinkQR } from "@/components/LeadLinkQR";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Users, Pencil, Link2, MapPin } from "lucide-react";

export default function RMManagement() {
  const [rms, setRms] = useState([]);
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ username: "", name: "", password: "", email: "", phone: "", monthly_target: 600000, mela_venue: "" });
  const [editRm, setEditRm] = useState(null);
  const [ef, setEf] = useState({ name: "", email: "", phone: "", monthly_target: 0, password: "", mela_venue: "" });

  const openEdit = (r) => {
    setEditRm(r);
    setEf({ name: r.name, email: r.email || "", phone: r.phone || "", monthly_target: r.monthly_target, password: "", mela_venue: r.mela_venue || "" });
  };
  const saveEdit = async () => {
    const payload = { name: ef.name, email: ef.email, phone: ef.phone, monthly_target: Number(ef.monthly_target), mela_venue: ef.mela_venue };
    if (ef.password) payload.password = ef.password;
    try {
      await api.patch(`/rms/${editRm.id}`, payload);
      toast.success("RM updated"); setEditRm(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = () => api.get("/rms").then((r) => setRms(r.data));
  useEffect(() => {
    load();
    api.get("/melas").then((r) => setVenues([...new Set((r.data || []).map((m) => m.venue).filter(Boolean))]));
  }, []);

  const create = async () => {
    if (!f.username || !f.name || !f.password) return toast.error("Username, name and password required");
    try {
      await api.post("/rms", { ...f, monthly_target: Number(f.monthly_target) });
      toast.success("RM account created"); setOpen(false);
      setF({ username: "", name: "", password: "", email: "", phone: "", monthly_target: 600000, mela_venue: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/rms/${id}`); toast.success("RM removed"); load(); };
  const copyLink = (r) => { navigator.clipboard.writeText(`${window.location.origin}/apply/${r.public_token}`); toast.success(`Lead form link for ${r.name} copied`); };
  const purgeDemo = async () => {
    if (!window.confirm("Remove all seeded demo RMs, sample customers and demo melas? Your real data stays.")) return;
    try {
      const { data } = await api.post("/admin/purge-demo");
      toast.success(`Cleared demo data: ${data.rms_removed} RMs, ${data.customers_removed} customers, ${data.melas_removed} melas`);
      load();
    } catch (e) { toast.error("Purge failed"); }
  };

  return (
    <div>
      <PageHeader title="Manage RMs" subtitle="Create and manage relationship manager accounts."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={purgeDemo} data-testid="purge-demo-btn" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={14} className="mr-1" />Clear Demo Data</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="bg-[#0F4C81] hover:bg-[#0A3A66]" data-testid="create-rm-btn"><UserPlus size={16} className="mr-2" />Create RM</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create RM Account</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div><Label className="mb-1.5 block">Full Name</Label><Input data-testid="rm-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Username</Label><Input data-testid="rm-username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Password</Label><Input type="text" data-testid="rm-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Monthly Target (₹)</Label><Input type="number" value={f.monthly_target} onChange={(e) => setF({ ...f, monthly_target: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label className="mb-1.5 block">Mela Venue Assigned</Label>
                  <Select value={f.mela_venue || "__none__"} onValueChange={(v) => setF({ ...f, mela_venue: v === "__none__" ? "" : v })}>
                    <SelectTrigger data-testid="rm-mela-venue"><SelectValue placeholder="Select a mela venue" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-rm-btn" className="bg-[#0F4C81] hover:bg-[#0A3A66]">Create Account</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {rms.map((r) => (
          <Card key={r.id} className="p-6" data-testid={`rm-card-${r.username}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#EBF3FA] text-[#0F4C81] flex items-center justify-center font-bold text-lg">{r.name[0]}</div>
              <div><div className="font-bold text-slate-900">{r.name}</div><div className="text-xs text-slate-400">@{r.username}</div></div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-400 flex items-center gap-1"><Users size={12} />Customers</div><div className="font-mono font-bold text-slate-800">{r.customer_count}</div></div>
              <div><div className="text-xs text-slate-400">Monthly Target</div><div className="font-mono font-bold text-slate-800">{inr(r.monthly_target)}</div></div>
            </div>
            {r.mela_venue && (
              <div className="mt-2 flex items-center gap-1.5 text-xs" data-testid={`rm-venue-${r.username}`}>
                <MapPin size={12} className="text-emerald-600" />
                <span className="text-slate-400">Mela Venue:</span>
                <span className="font-medium text-slate-700">{r.mela_venue}</span>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <Button variant="ghost" size="sm" className="flex-1 text-[#0F4C81] hover:bg-[#EBF3FA]" onClick={() => copyLink(r)} data-testid={`copy-link-${r.username}`}><Link2 size={14} className="mr-1" />Copy Link</Button>
              <LeadLinkQR url={`${window.location.origin}/apply/${r.public_token}`} name={r.name} label="QR" triggerClass="flex-1" testid={`qr-${r.username}`} />
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(r)} data-testid={`edit-rm-${r.username}`}><Pencil size={14} className="mr-1" />Edit</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="flex-1 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-rm-${r.username}`}><Trash2 size={14} className="mr-1" />Remove</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Remove {r.name}?</AlertDialogTitle><AlertDialogDescription>This deactivates the account. Their customers remain in the database and can be reassigned.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(r.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-${r.username}`}>Remove</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
        {rms.length === 0 && <div className="text-slate-400">No RMs yet. Create the first one.</div>}
      </div>

      <Dialog open={!!editRm} onOpenChange={(o) => !o && setEditRm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {editRm?.name}</DialogTitle><DialogDescription>Update the RM's details, monthly target, or reset their password.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div><Label className="mb-1.5 block">Full Name</Label><Input data-testid="edit-rm-name" value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Phone</Label><Input value={ef.phone} onChange={(e) => setEf({ ...ef, phone: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Email</Label><Input value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Monthly Target (₹)</Label><Input type="number" data-testid="edit-rm-target" value={ef.monthly_target} onChange={(e) => setEf({ ...ef, monthly_target: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="mb-1.5 block">Mela Venue Assigned</Label>
              <Select value={ef.mela_venue || "__none__"} onValueChange={(v) => setEf({ ...ef, mela_venue: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="edit-rm-mela-venue"><SelectValue placeholder="Select a mela venue" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {venues.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="mb-1.5 block">New Password <span className="text-slate-400 font-normal">(leave blank to keep current)</span></Label><Input type="password" data-testid="edit-rm-password" value={ef.password} onChange={(e) => setEf({ ...ef, password: e.target.value })} placeholder="••••••••" /></div>
          </div>
          <DialogFooter><Button onClick={saveEdit} data-testid="save-edit-rm-btn" className="bg-[#0F4C81] hover:bg-[#0A3A66]">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
